import { useState, useEffect } from 'react';
import { authenticatedFetch, getUser } from './auth';

export interface PendingApprovalCounts {
  domains: number;
  adminRequests: number;
  total: number;
}

/**
 * Hjelpefunksjon for komponenter som godkjenner/avviser — kalles etter en handling
 * og varsler alle aktive `usePendingApprovalCounts`-instanser om å re-hente.
 */
export function notifyApprovalsChanged() {
  window.dispatchEvent(new Event('pending-approvals-changed'));
}

/**
 * Henter antall ventende godkjenninger for studieforbund/systemadmin.
 * - Polles hvert 60. sekund som fallback
 * - Lytter også på custom event 'pending-approvals-changed' for umiddelbar oppdatering
 *   når brukeren godkjenner/avviser i en av godkjenningssidene
 */
export function usePendingApprovalCounts(): PendingApprovalCounts {
  const [counts, setCounts] = useState<PendingApprovalCounts>({ domains: 0, adminRequests: 0, total: 0 });

  useEffect(() => {
    // Backend krever admin/systemadmin (403 for alle andre). Skip fetch for member/sensor
    // for å unngå unødvendige 403-kall (støy i console + nettverkstrafikk).
    const user = getUser();
    if (!user || (user.role !== 'admin' && user.role !== 'systemadmin')) {
      return;
    }

    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const [domR, adminR] = await Promise.all([
          authenticatedFetch('/api/admin/email-domains/pending-count'),
          authenticatedFetch('/api/admin/member-admin-requests/pending-count')
        ]);
        let domains = 0, adminRequests = 0;
        if (domR.ok) {
          const d = await domR.json();
          domains = d.count || 0;
        }
        if (adminR.ok) {
          const d = await adminR.json();
          adminRequests = d.count || 0;
        }
        if (!cancelled) {
          setCounts({ domains, adminRequests, total: domains + adminRequests });
        }
      } catch {
        // ignore — failed fetch shouldn't crash UI
      }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    const handler = () => fetchCounts();
    window.addEventListener('pending-approvals-changed', handler);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('pending-approvals-changed', handler);
    };
  }, []);

  return counts;
}
